import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { gql, useMutation } from '@apollo/client';
import { ClipboardList, X, ChevronRight } from 'lucide-react';
import ReadinessAssessmentFlow from '../PlanBuilder/pages/ReadinessAssessmentFlow';

const SKIP_READINESS_ASSESSMENT = gql`
  mutation SkipReadinessAssessment {
    skipReadinessAssessment {
      result {
        id
        skipped
        completed
      }
      errors
    }
  }
`;

/**
 * ReadinessAssessmentPrompt
 *
 * A dismissible banner shown post-onboarding on the LaunchPad when:
 * - The succession_readiness feature is enabled for the company
 * - The user has not yet completed or permanently skipped the assessment
 *
 * "Take the assessment" → opens the ReadinessAssessmentFlow modal
 * "Skip for now" → calls skipReadinessAssessment mutation (survives page reload)
 *
 * Props:
 * - planSlug: PlanBuilder template slug for this company's assessment
 * - onComplete: (result) => void   — called after assessment is submitted
 * - onSkip: () => void             — called after skip is confirmed
 */
const ReadinessAssessmentPrompt = ({ planSlug = 'succession-readiness-assessment', onComplete, onSkip }) => {
  const [showFlow, setShowFlow] = useState(false);
  const [skipAssessment, { loading: skipping }] = useMutation(SKIP_READINESS_ASSESSMENT);

  // `readiness_assessment_skipped` is emitted server-side from the
  // SkipReadinessAssessment mutation. It used to fire here, *before* the
  // mutation — and since the catch below deliberately hides the prompt even
  // when the mutation fails, a failed skip still recorded a skip.
  const handleSkip = async () => {
    try {
      await skipAssessment();
      onSkip?.();
    } catch (err) {
      console.error('Failed to skip readiness assessment:', err);
      // Still hide the prompt locally even if the mutation fails
      onSkip?.();
    }
  };

  const handleComplete = (result) => {
    setShowFlow(false);
    onComplete?.(result);
  };

  const handleCloseFlow = () => {
    setShowFlow(false);
  };

  return (
    <>
      <div
        className="relative rounded-xl border border-succession-border overflow-hidden"
        style={{
          background:
            'linear-gradient(to bottom right, rgba(var(--succession-primary-rgb, 84,137,182), 0.08), rgba(var(--succession-secondary-rgb, 0,41,73), 0.04))',
        }}>
        {/* Dismiss (skip) button */}
        <button
          type="button"
          onClick={handleSkip}
          disabled={skipping}
          className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-succession-bg-tertiary transition-colors z-10"
          aria-label="Skip assessment prompt">
          <X className="w-4 h-4 text-succession-text-secondary" />
        </button>

        <div className="p-5 md:p-6 flex items-start gap-4 pr-12">
          {/* Icon */}
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{
              backgroundColor: 'rgba(var(--succession-primary-rgb, 84,137,182), 0.15)',
              color: 'var(--succession-primary, #5489B6)',
            }}>
            <ClipboardList className="w-5 h-5" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-succession-text-primary text-sm">
              How ready are you for succession?
            </p>
            <p className="text-xs text-succession-text-secondary mt-1 leading-relaxed">
              Answer a few quick questions to get personalized recommendations for your next steps.
            </p>

            <div className="flex items-center gap-4 mt-3">
              <button
                type="button"
                onClick={() => {
                  try {
                    if (window.ahoy) window.ahoy.track('readiness_assessment_started');
                  } catch (_) { /* ahoy optional */ }
                  setShowFlow(true);
                }}
                className="inline-flex items-center gap-1.5 text-sm font-medium transition-opacity hover:opacity-80"
                style={{ color: 'var(--succession-primary, #5489B6)' }}>
                Take the assessment
                <ChevronRight className="w-3.5 h-3.5" />
              </button>

              <button
                type="button"
                onClick={handleSkip}
                disabled={skipping}
                className="text-xs text-succession-text-secondary hover:text-succession-text-primary transition-colors">
                Skip for now
              </button>
            </div>
          </div>
        </div>
      </div>

      {showFlow && (
        <ReadinessAssessmentFlow
          planSlug={planSlug}
          onComplete={handleComplete}
          onClose={handleCloseFlow}
        />
      )}
    </>
  );
};

ReadinessAssessmentPrompt.propTypes = {
  planSlug: PropTypes.string,
  onComplete: PropTypes.func,
  onSkip: PropTypes.func,
};

ReadinessAssessmentPrompt.defaultProps = {
  planSlug: 'succession-readiness-assessment',
  onComplete: undefined,
  onSkip: undefined,
};

export default ReadinessAssessmentPrompt;
